// src/components/Posts/PostForm.jsx
import React, {
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { Paper } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';

import { ResizableImage } from '../../tiptap/ResizableImage';
import { ResizableVideo } from '../../tiptap/ResizableVideo';           // ✅
import EditorToolbar from '../../tiptap/EditorToolbar';
import api from '../../api/axios';
import { FontSize } from '../../tiptap/FontSize'

import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';



/**
 * 본문 전용 에디터
 * - 부모(ref) ↔ insertImage(file), insertVideo(file), getContent()
 */
const PostForm = forwardRef(
  (
    { initialContent = '', onSubmit, isEdit = false, disabled = false },
    ref,
  ) => {
    /* ── TipTap ───────────────────────────── */
    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          table: false, // StarterKit 내부 Table 비활성화
        }),
        Link.configure({
          autolink: true,         // 타이핑 중 URL 자동 링크 
          linkOnPaste: true,      // 붙여넣기 시 자동 링크 
          openOnClick: false,     // 편집 중에는 클릭해도 바로 이동하지 않음 
          HTMLAttributes: {
            target: '_blank',
            rel: 'noopener noreferrer nofollow',
            class: 'tiptap-link',
          },
          validate: href => /^https?:\/\/|^mailto:|^tel:/i.test(href), // 허용할 링크만 
        }),

        Table.configure({
          resizable: true,
          HTMLAttributes: {
            class: 'my-table',  // 선택적 클래스 (스타일 조정 용도)
          },
        }),
        TableRow,
        TableHeader,
        TableCell,

        Underline,
        ResizableImage,
        ResizableVideo,
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        FontSize,
      ],
      content: initialContent || '<p></p>',
    });
    /* 초기 내용 주입 */
    useEffect(() => {
      if (editor && initialContent) editor.commands.setContent(initialContent);
    }, [initialContent, editor]);

    /* 이미지 업로드 & 삽입 ----------------------------------- */
    const insertImage = async (file) => {
      if (!editor || !file) return;

      const fd = new FormData();
      fd.append('image', file);
      const { data } = await api.post('/api/posts/api/upload/image/', fd);

      const url = data.url.startsWith('http')
        ? data.url
        : process.env.NODE_ENV === 'development'
          ? `http://localhost:8000${data.url}`
          : `https://sandle.it.com${data.url}`;

      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'resizableImage',
            attrs: { src: url, width: 300, height: 300 },
          },
          { type: 'paragraph' },
        ])
        .run();
    };

    /* 영상 업로드 & 삽입 ------------------------------------- */
    const insertVideo = async (file) => {                          // ✅
      if (!editor || !file) return;

      const fd = new FormData();
      fd.append('video', file);
      const { data } = await api.post('/api/posts/api/upload/video/', fd);

      const url = data.url.startsWith('http')
        ? data.url
        : process.env.NODE_ENV === 'development'
          ? `http://localhost:8000${data.url}`
          : `https://sandle.it.com${data.url}`;

      editor
        .chain()
        .focus()
        .insertContent([
          {
            type: 'resizableVideo',
            attrs: { src: url, width: 480, height: 270 },
          },
          { type: 'paragraph' },
        ])
        .run();
    };

    /* 부모에서 사용할 API 노출 -------------------------------- */
    useImperativeHandle(ref, () => ({
      insertImage,
      insertVideo,                                                // ✅
      getContent: () => editor?.getHTML() || '',
    }));

    /* ── UI ──────────────────────────────── */
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 4 },
          border: '1px solid #ccc',
          borderRadius: 2,
        }}
        onClick={() => editor?.commands.focus()} // 빈 곳 클릭 시 포커스
      >
        {/* 툴바 */}
        <EditorToolbar editor={editor} />

        {/* 에디터 */}
        <EditorContent
          editor={editor}
          className="tiptap"
          style={{
            padding: 16,
            minHeight: 200,
            backgroundColor: '#fff',
            outline: 'none',
            cursor: 'text',
            textAlign: 'left',

          }}
        />
      </Paper>
    );
  },
);

export default PostForm;
